import Button from 'components/ButtonDeprecated';
import Columns2 from 'components/Columns2';
import { Container } from 'flux/utils';
import Select, { Option } from 'components/SelectDeprecated';
import Fieldset from 'components/Fieldset';
import FormElement from 'components/FormElement';
import * as React from 'react';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import SettingsContainer from './SettingsContainer';
import SettingsStore from 'stores/SettingsStore';
import ToastActions from 'actions/ToastActions';
import UserActions from 'user/UserActions';
import LinkConstants from 'constants/LinkConstants';
import CurrentUser from 'auth/CurrentUser';

interface LanguageElement {
  [key: string]: string;
}

type LanguageDict = {
  primary?: LanguageElement;
  secondary: LanguageElement;
};

type LanguageMutation = {
  variables: {
    input?: {
      targetType?: 'group' | 'organization';
      targetUuid?: string; // this is the uuid of the group or organization context being used to change the preferred language
      userUuid?: string; // this is the uuid of the of the user who's preferred language is being changed.
      preferredLanguageCode?: string; // new language preference for the given user
    };
  };
};

type Props = {
  mutate?: (dict: LanguageMutation) => Promise<void>;
  preferredLanguage?: string; // new language preference for the given user
  targetUuid?: string; // this is the uuid of the group or organization context being used to change the preferred language
  targetType?: 'group' | 'organization';
  userUuid?: string; // this is the uuid of the of the user who's preferred language is being changed.
  onPreferredLanguageUpdated?: () => void; // handler for refreshing the user after update
  onRequestHide?: () => {};
  hideHeader?: boolean;
  full?: boolean;
};

type State = {
  canSubmit: boolean;
  chosenLanguage?: string;
  languageOpts: Option[];
};

class UserPreferredLanguageForm extends React.Component<Props, State> {
  static getStores() {
    return [SettingsStore];
  }

  static calculateState() {
    const languages: LanguageDict = SettingsStore.getSetting('languages');
    const languageOpts = [{ value: 'none', label: 'No preference set' }].concat(
      Object.keys(languages.secondary).map((langCode: string) => ({
        value: langCode,
        label: languages.secondary[langCode],
      }))
    );

    return {
      canSubmit: false,
      languageOpts,
    };
  }

  disableButton = () => {
    this.setState({ canSubmit: false });
  };

  enableButton = () => {
    this.setState({ canSubmit: true });
  };

  submit = (lang: string) => {
    const reset = () => {
      this.disableButton();
    };

    const onSuccess = () => {
      ToastActions.showSuccessToast('Changes saved');
      if (this.props.onPreferredLanguageUpdated) {
        this.props.onPreferredLanguageUpdated();
      }
    };

    const onFailure = (error: any) => {
      ToastActions.showAPIError(error);
      ToastActions.showDefaultErrorToast();
      if (this.props.onRequestHide) {
        this.props.onRequestHide();
      }
    };

    const { mutate, userUuid, targetUuid, targetType } = this.props;

    // The case where we're handling updates at a outer mutation
    // wrapper level
    if (mutate) {
      mutate({
        variables: {
          input: {
            userUuid,
            targetUuid,
            targetType,
            preferredLanguageCode: lang,
          },
        },
      })
        .then(reset)
        .then(onSuccess, onFailure);
    } else {
      UserActions.savePreference('preferred_language', lang).then(reset).then(onSuccess, onFailure);
    }
  };

  render() {
    const preferredLanguage = this.props.preferredLanguage ?? 'none';
    const { userUuid } = this.props;

    const formElement = (
      <div>
        <FormElement
          data-test="user-preferred-language-select"
          label=""
          ref="preferred_language"
          onValidChange={(value) => {
            if (value === preferredLanguage) {
              this.disableButton();
            } else {
              this.enableButton();
            }
            this.setState({ chosenLanguage: value });
          }}
          validate={(value) => {
            const invalid = value.length < 1;
            if (invalid) {
              this.disableButton();
            }
          }}
          element={Select}
          elementProps={{
            options: this.state.languageOpts,
            value: this.state.chosenLanguage ?? preferredLanguage,
          }}
        />
      </div>
    );

    return (
      <form
        data-test="user-preferred-language-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (this.state.chosenLanguage) {
            this.submit(this.state.chosenLanguage);
          }
        }}
      >
        <Fieldset legend={!this.props.hideHeader ? 'Preferred language' : undefined}>
          <Row size="xxsmall">
            <Text align="left">
              {CurrentUser.getUser().getUuid() === userUuid
                ? 'Messages you receive will be translated to your preferred language. '
                : 'This person will receive messages in the preferred language you select. '}
              <Link to={LinkConstants.PREFERRED_LANGUAGE_SETTING} target="_blank">
                Learn more.
              </Link>
            </Text>
          </Row>
          <SettingsContainer>
            {this.props.full ? formElement : <Columns2 gutter="small">{formElement}</Columns2>}
          </SettingsContainer>
          <Button
            size="small"
            type="submit"
            disabled={!this.state.canSubmit}
            data-test="user-preferred-language-save-button"
          >
            Save
          </Button>
        </Fieldset>
      </form>
    );
  }
}

export default Container.create<Props, State>(UserPreferredLanguageForm);
